import apiClient from '../config/api';
import { User } from '../types';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface RegisterData {
  email: string;
  password: string;
  nom: string;
  prenom: string;
  entreprise: string;
  siret: string; // Obligatoire (lié à la période d'essai)
  corpsMetier: string; // Obligatoire (corps de métier de l'artisan)
  telephone?: string;
  adresse?: string;
  siren?: string;
  codePaysIso?: string;
  numTvaIntracom?: string;
  isExemptFromTVA?: boolean; // Statut TVA détecté lors de la validation SIRET
}

export interface ChangePasswordData {
  oldPassword: string;
  newPassword: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  newPassword: string;
}

export interface SiretValidationResponse {
  valid: boolean;
  message: string;
  siret: string;
  siren?: string;
  denominationSociale?: string;
  adresse?: string;
  categorieJuridique?: string;
  isExemptFromTVA?: boolean;
  numTVAIntracommunautaire?: string;
  alreadyUsedTrial: boolean;
  trialWarning?: string;
}

class AuthService {
  /**
   * Connexion d'un utilisateur
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/auth/login', credentials);
    return response.data;
  }

  /**
   * Inscription d'un nouvel utilisateur
   */
  async register(data: RegisterData): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/api/auth/register', data);
    return response.data;
  }

  /**
   * Déconnexion
   */
  async logout(): Promise<void> {
    // Optionnel: appeler l'endpoint de logout si votre backend en a un
    // await apiClient.post('/api/auth/logout');
    localStorage.removeItem('momo_auth');
  }

  /**
   * Récupérer les informations de l'utilisateur connecté
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/api/auth/me');
    return response.data;
  }

  /**
   * Mettre à jour le profil de l'utilisateur
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await apiClient.put<User>('/api/auth/profile', data);
    return response.data;
  }

  /**
   * Modifier le mot de passe de l'utilisateur connecté
   * Requiert: Token JWT dans Authorization header
   */
  async changePassword(data: ChangePasswordData): Promise<string> {
    const response = await apiClient.post<string>('/api/auth/change-password', data);
    return response.data;
  }

  /**
   * Demander la réinitialisation du mot de passe (mot de passe oublié)
   * Envoie un email avec un lien de réinitialisation
   * Endpoint public (pas d'authentification requise)
   */
  async forgotPassword(data: ForgotPasswordData): Promise<string> {
    const response = await apiClient.post<string>('/api/auth/forgot-password', data);
    return response.data;
  }

  /**
   * Réinitialiser le mot de passe avec le token reçu par email
   * Endpoint public (pas d'authentification requise)
   */
  async resetPassword(data: ResetPasswordData): Promise<string> {
    const response = await apiClient.post<string>('/api/auth/reset-password', data);
    return response.data;
  }

  /**
   * Valider un SIRET en temps réel et récupérer les informations de l'entreprise
   * Endpoint public (pas d'authentification requise)
   */
  async validateSiret(siret: string): Promise<SiretValidationResponse> {
    const response = await apiClient.get<SiretValidationResponse>(`/api/auth/validate-siret/${siret}`);
    return response.data;
  }
}

export default new AuthService();
